import { useEffect } from "react";

// Balises SEO par route (titre, description, canonique, robots, Open Graph, rel prev/next, <html lang>).
// Le DOM rendu est ce que Googlebot indexe : chaque page publique déclare ses balises avec useSeo(),
// restaurées au démontage pour que la route suivante reparte des valeurs d'index.html.
export const PUBLIC_ORIGIN = String(process.env.REACT_APP_PUBLIC_URL || "https://yalola.app").replace(/\/+$/, "");
export const DEFAULT_TITLE = "Yalola — Le réseau chrétien qui rassemble";
export const DEFAULT_DESC = "Yalola — Le réseau chrétien qui rassemble. Partagez, priez, adorez ensemble.";
const DEFAULT_IMAGE = `${PUBLIC_ORIGIN}/api/e/app/share.jpg`;

export const canonicalUrl = (path) => `${PUBLIC_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;

const head = () => document.head;
const meta = (sel) => head().querySelector(sel);

const ensureMeta = (attr, name) => {
  let el = meta(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    el.setAttribute("data-seo", "1");
    head().appendChild(el);
  }
  return el;
};

const ensureLink = (rel) => {
  let el = meta(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    el.setAttribute("data-seo", "1");
    head().appendChild(el);
  }
  return el;
};

const setOrRemove = (el, attr, value) => {
  if (value) el.setAttribute(attr, value);
  else if (el.getAttribute("data-seo") === "1") el.remove();
  else el.removeAttribute(attr);
};

// Page qui a posé ses propres balises (useSeo actif) : syncOgUrl() ne les écrase pas.
let _pageSeoHref = null;

// Applique les balises et renvoie une fonction de restauration.
// `url` : og:url de partage si différente de la canonique (ex. reel : canonique /post/<id>, partage /reels?start=<id>).
export function applySeo({ title, description, canonical, robots, image, type = "website", prev, next, lang, url } = {}) {
  const prevTitle = document.title;
  const mdEl = ensureMeta("name", "description");
  const prevDesc = mdEl.getAttribute("content");

  document.title = title ? (title.includes("Yalola") ? title : `${title} — Yalola`) : DEFAULT_TITLE;
  const desc = (description || DEFAULT_DESC).replace(/\s+/g, " ").trim().slice(0, 300);
  mdEl.setAttribute("content", desc);
  if (lang) document.documentElement.lang = lang;

  // canonical === false : aucune canonique (page « introuvable » en noindex — ne jamais pointer une autre page).
  const canonicalHref = canonical === false ? "" : canonical ? canonicalUrl(canonical) : canonicalUrl(window.location.pathname + window.location.search);
  setOrRemove(ensureLink("canonical"), "href", canonicalHref);
  const robotsEl = ensureMeta("name", "robots");
  robotsEl.setAttribute("content", robots || "index,follow");
  setOrRemove(ensureLink("prev"), "href", prev ? canonicalUrl(prev) : "");
  setOrRemove(ensureLink("next"), "href", next ? canonicalUrl(next) : "");

  const og = {
    "og:title": document.title, "og:description": desc, "og:url": url ? canonicalUrl(url) : (canonicalHref || canonicalUrl(window.location.pathname)), "og:type": type,
    "og:image": image || DEFAULT_IMAGE, "og:image:secure_url": image || DEFAULT_IMAGE,
  };
  Object.entries(og).forEach(([k, v]) => ensureMeta("property", k).setAttribute("content", v));
  ensureMeta("name", "twitter:title").setAttribute("content", document.title);
  ensureMeta("name", "twitter:description").setAttribute("content", desc);
  ensureMeta("name", "twitter:image").setAttribute("content", image || DEFAULT_IMAGE);
  _pageSeoHref = window.location.href;

  return () => {
    _pageSeoHref = null;
    document.title = prevTitle;
    if (prevDesc !== null) mdEl.setAttribute("content", prevDesc);
    // <html lang> n'est PAS restauré : la langue de l'interface est globale (AppContext) — la remettre à
    // l'ancienne valeur au démontage désynchronisait la page suivante après un changement de langue.
    head().querySelectorAll('link[rel="prev"][data-seo], link[rel="next"][data-seo], meta[name="robots"][data-seo]').forEach((el) => el.remove());
  };
}

// Pages SANS useSeo (prières, marketplace, live…) : og:url = URL réelle de la page (index.html porte la racine pour toutes,
// ce qui faisait partager le MÊME aperçu Facebook mis en cache pour chaque lien). Appelé à chaque changement de route.
export function syncOgUrl() {
  if (_pageSeoHref === window.location.href) return false;
  ensureMeta("property", "og:url").setAttribute("content", canonicalUrl(window.location.pathname + window.location.search));
  return true;
}

// Hook : `deps` = valeurs dont dépendent les balises (ex. le post chargé). `enabled=false` → rien.
export function useSeo(opts, deps = []) {
  useEffect(() => {
    if (opts === null || opts === false || opts?.enabled === false) return undefined;
    return applySeo(opts || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Extrait court d'un texte de publication pour un titre / une description.
export const excerpt = (text, max = 150) => {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), 40)).trim()}…`;
};
